import { useState, useCallback, useMemo, useRef } from "react";
import { useSelector } from 'react-redux';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import {
  useMediaQuery,
  useTheme,
  Box,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  ToggleButton,
} from "@mui/material";
import { FilterList, Group, ToggleOn, ToggleOff } from "@mui/icons-material";
import useAuthCheck from 'hooks/useAuthCheck';
import {
  LAYOUT_HEIGHT,
  DIVERT_RETURN_STATUS,
  EXCLUDED_FLIGHT_STATUS,
} from 'state/constants';
import { fetchLegMembersByFlt } from 'state/api';
import {
  capitalizeFirstLetter,
  removeItemByValueImmutable,
  filterItemByValueImmutable,
} from 'state/helpers';
import { filterFlights } from "./helper";
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import StyledButton from 'components/StyledButton';
import SearchInput from 'components/SearchInput';
import CrewListDialog from 'components/CrewListDialog';
import FlightList from 'scenes/flightSchedule/FlightList';
import DynamicTable from "./DynamicTable";
import FilterDialog from "./FilterDialog";


const EMPTY_FILTER = { logicOperator: "AND", rules: [] };

const FlightView = () => {
  useAuthCheck();
  const timeMode = useSelector((state) => state.timemode);
  const user     = useSelector((state) => state.user);
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canView = hasRight(permissionMap, "FlightView", "view", false);

  const scrollRef = useRef(null);
  const [switched, setSwitched] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
  const [flight, setFlight]     = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [jumpList, setJumpList] = useState([]);

  const flightsData = useSelector((state) => state.flights || []);

  const filteredFlights = useMemo(() =>
    switched
      ? removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
      : filterItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
  , [flightsData, switched]);

  const nonDivFlights = useMemo(() =>
    filteredFlights.filter(f => !DIVERT_RETURN_STATUS.includes(f.flightStatus))
  , [filteredFlights]);

  const [filterState, setFilterState]   = useState(EMPTY_FILTER);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTER);
  const [groupByField, setGroupByField]   = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [groupAnchorEl, setGroupAnchorEl]       = useState(null);

  const formatTime = (key, timeMode) => (timeMode === 'UTC' ? key : `${key}LocalBase`);

  const customColumns = useMemo(() => {
    const hiddenKeysWhenSwitched = new Set(['etd', 'eta', 'atd', 'ata', 'noOfPax']);
    const timeColumns = ['std', 'sta', 'etd', 'eta', 'atd', 'ata']
      .filter(k => !(!switched && hiddenKeysWhenSwitched.has(k)))
      .map(k => ({
        key: formatTime(k, timeMode),
        label: capitalizeFirstLetter(k),
        type: 'string',
        sortable: true,
        searchable: false,
      }));

    return [
      { key: '__rowNumber', label: 'No', type: 'number', sortable: false, searchable: false },
      { key: formatTime('flightDate', timeMode), label: 'Date', type: 'date', sortable: true, searchable: true },
      { key: 'flightNo',  label: 'Flight No', type: 'string', sortable: true, searchable: true },
      { key: 'sector',    label: 'Sector',    type: 'string', sortable: true, searchable: true },
      { key: 'acType',    label: 'AC Type',   type: 'string', sortable: true, searchable: true },
      { key: 'flightReg', label: 'Reg',       type: 'string', sortable: true, searchable: true },
      ...timeColumns,
      switched && { key: 'noOfPax', label: 'Pax', type: 'number', sortable: true, searchable: true },
      {
        key: switched ? 'remarks' : 'flightStatus',
        label: switched ? 'Remark' : 'Status',
        type: 'string',
        sortable: true,
        searchable: true,
        maxWidth: 180,
      },
    ].filter(Boolean);
  }, [timeMode, switched]);

  const filteredData = useMemo(() =>
    filterFlights({ data: nonDivFlights, searchQuery, filters: activeFilters, columns: customColumns })
  , [nonDivFlights, searchQuery, activeFilters, customColumns]);

  const handleViewClick = useCallback(async (param) => {
    try {
      const { flightDate, flightDesc, dep } = param;
      const { flightDetails, legMembers, jumpSeaters } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);
      setFlight(flightDetails);
      setCrewList(legMembers);
      setJumpList(jumpSeaters);
      setOpenCrewListDialog(true);
    } catch {
      setOpenCrewListDialog(false);
    }
  }, []);

  const handleCloseCrewListDialog = () => {
    setFlight([]);
    setCrewList([]);
    setJumpList([]);
    setOpenCrewListDialog(false);
  };

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Flight View"
          subtitle={`List of ${filteredData?.length || 0} Flights`}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <SearchInput
            widthMobile="150px"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Tooltip title="Filter" arrow>
            <span>
              <StyledButton onClick={() => setFilterDialogOpen(true)}>
                <FilterList />
              </StyledButton>
            </span>
          </Tooltip>

          {!isMobile && (
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Group" arrow>
                <span>
                  <StyledButton onClick={(e) => setGroupAnchorEl(e.currentTarget)}>
                    <Group />
                  </StyledButton>
                </span>
              </Tooltip>

              <Menu
                anchorEl={groupAnchorEl}
                open={Boolean(groupAnchorEl)}
                onClose={() => setGroupAnchorEl(null)}
              >
                <MenuItem
                  selected={!groupByField}
                  onClick={() => { setGroupByField(null); setGroupAnchorEl(null); }}
                >
                  Reset
                </MenuItem>
                <Divider />
                {customColumns
                  .filter(c => c.key !== "__rowNumber")
                  .map(col => (
                    <MenuItem
                      key={col.key}
                      selected={groupByField === col.key}
                      onClick={() => {
                        setGroupByField(prev => prev === col.key ? null : col.key);
                        setGroupAnchorEl(null);
                      }}
                    >
                      {col.label}
                    </MenuItem>
                  ))
                }
              </Menu>

              <Tooltip title={switched ? "Switch to Cancelled" : "Switch to Scheduled"} arrow>
                <ToggleButton
                  size="small"
                  value="switched"
                  selected={switched}
                  onChange={() => setSwitched(prev => !prev)}
                >
                  {switched ? <ToggleOn /> : <ToggleOff />}
                </ToggleButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </FlexBetween>

      <Box 
        display="grid" 
        gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
        gap={2}
      >
        <Box 
          ref={scrollRef} 
          height={LAYOUT_HEIGHT} 
          sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
          {isMobile ? (
            <FlightList 
              flights={filteredData} 
              onClick={canView ? handleViewClick : undefined} 
            />
          ) : (
            <Card>
              <CardContent sx={{ px: 0, pt: 0, '&:last-child': { pb: 0 } }}>
                <DynamicTable
                  data={filteredData}
                  columns={customColumns}
                  pageSize={50}
                  groupByField={groupByField}
                  onRowClick={canView ? handleViewClick : undefined}
                />
              </CardContent>
            </Card>
          )}
        </Box>
        <ScrollToTop scrollRef={scrollRef} />
      </Box>

      {filterDialogOpen && (
        <FilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          filterState={filterState}
          setFilterState={setFilterState}
          activeColumns={customColumns}
          onClear={() => { setActiveFilters(EMPTY_FILTER); setFilterDialogOpen(false); }}
          onApply={() => { setActiveFilters(filterState); setFilterDialogOpen(false); }}
        />
      )}

      {openCrewListDialog && (
        <CrewListDialog
          open={openCrewListDialog}
          onClose={handleCloseCrewListDialog}
          flight={flight}
          crewList={crewList}
          jumpList={jumpList}
        />
      )}
    </Box>
  );
};

export default FlightView;
